import { Modal } from "antd";
import { Document, Page, pdfjs } from "react-pdf";
import { useState, useEffect } from "react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

export const FileViewerModal: React.FC<{
  open: boolean;
  onClose: () => void;
  fileUrl: string | null;
  fileType?: "pdf" | "image";
}> = ({ open, onClose, fileUrl, fileType = "pdf" }) => {
  const [numPages, setNumPages] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (open) {
      setNumPages(0);
      setPdfLoading(true);
      setPdfError(null);
      setImageLoading(true);
      setImageError(false);
    }
  }, [open, fileUrl]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      styles={{ body: { maxHeight: "80vh", overflowY: "auto", padding: 0 } }}
    >
      {fileUrl && fileType === "image" && (
        <div className="relative min-h-[300px] flex items-center justify-center">
          {imageLoading && !imageError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <div className="h-8 w-8 border-4 border-gray-200 border-t-primary rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Loading image…</p>
            </div>
          )}

          {imageError && (
            <p className="text-sm text-red-500 p-6">
              Failed to load image. Please try again.
            </p>
          )}

          <img
            src={fileUrl}
            alt="certificate"
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageLoading(false);
              setImageError(true);
            }}
            style={{
              display: imageLoading || imageError ? "none" : "block",
              width: "100%",
              height: "auto",
            }}
          />
        </div>
      )}

      {/* ---------- PDF ---------- */}
      {fileUrl && fileType === "pdf" && (
        <div className="relative min-h-[300px]">
          {pdfLoading && !pdfError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
              <div className="h-8 w-8 border-4 border-gray-200 border-t-primary rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Loading PDF…</p>
            </div>
          )}

          {pdfError && (
            <p className="text-sm text-red-500 p-6 text-center">
              Failed to load PDF. {pdfError}
            </p>
          )}

          <Document
            file={fileUrl}
            onLoadSuccess={({ numPages }) => {
              setNumPages(numPages);
              setPdfLoading(false);
            }}
            onLoadError={(error) => {
              console.error("PDF load error:", error);
              setPdfError(error?.message ?? "");
              setPdfLoading(false);
            }}
            loading={null}
            error={null}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <Page
                key={i}
                pageNumber={i + 1}
                width={860}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            ))}
          </Document>
        </div>
      )}
    </Modal>
  );
};
